import {useQuery, useQueryClient} from '@tanstack/react-query';
import {SecurityTypes} from '@qep/types/src/index';
import {symbolLookup} from '../services/symbols';
import {beginFlowStage, endFlowStage} from '../utils/audit';
import {FuturesSpreadProduct, Product} from '../utils/Product';

/**
 * React Query hook that performs a backend symbol lookup and returns a list of
 * instantiated `Product` / `FuturesSpreadProduct` objects. Each product is
 * also seeded into the shared product query cache so downstream consumers can
 * read the latest snapshot without refetching.
 *
 * The optional `tradeable` scope defaults to the historical tradable-only
 * behavior. It is part of the query key so callers with different scopes do
 * not reuse each other's search results.
 *
 * The query is disabled for empty search terms so focusing the picker does
 * not issue a network request until the user starts typing.
 */
export function useSymbolSearch(
  searchTerm: string,
  securityTypes: SecurityTypes[],
  exchanges: string[],
  tradeable = true,
) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['symbolSearch', searchTerm, securityTypes, exchanges, tradeable],
    async queryFn() {
      const token = beginFlowStage('order-entry', 'symbolLookup');
      const symbols = await symbolLookup({
        symbol: searchTerm, secTypes: securityTypes, exchanges, tradeable,
      });
      endFlowStage(token, 'order-entry', 'symbolLookup');
      const products = symbols.map(symbol =>
        symbol.secType === SecurityTypes.FuturesSpread
          ? new FuturesSpreadProduct(symbol, symbol.legs ?? [])
          : new Product(symbol));
      for (const product of products) {
        queryClient.setQueryData(['product', product.exchangeSymbol, product.segmentMic], product);
      }

      return products;
    },
    staleTime: 1000 * 30,
    enabled: searchTerm?.length > 0,
  });
}
